import { PUBLIC_WEB_ORIGIN } from './platform';
import { parseRoute } from './router';

/** Custom-scheme fallback (spec §6), registered in `ios/App/App/Info.plist`. */
export const DEEP_LINK_PROTOCOL = 'lexicon:';

const WEB_HOST = new URL(PUBLIC_WEB_ORIGIN).host;

/**
 * A URL iOS hands the app → the in-app path to navigate to, or null when the
 * URL is not a challenge link this app owns.
 *
 * Pure, so the whole thing is testable without a device. Two accepted forms
 * (spec §6):
 *   - `https://lexicon-pnm.pages.dev/c/<code>` — the universal link, the one
 *     a friend actually receives;
 *   - `lexicon://c/<code>` — the custom scheme, which works on any device
 *     with the app installed, verified domain or not.
 *
 * The fragment is preserved verbatim: it carries the obfuscated word that
 * makes a link playable while the API is unreachable (spec §4.2). The
 * screen no longer reads it off `window.location.hash` directly — the
 * router passes it down as a prop instead.
 *
 * Path validity is delegated to `parseRoute` rather than re-derived here. If
 * the router would not render a challenge for the path, there is nothing to
 * open, and the two can never drift apart on what a code looks like.
 */
export function parseDeepLink(url: string): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  let pathname: string;
  if (parsed.protocol === DEEP_LINK_PROTOCOL) {
    // `lexicon://c/Ab3xY` parses with host `c` and pathname `/Ab3xY`, so the
    // authority has to be folded back in; `lexicon:///c/Ab3xY` (empty
    // authority) and `lexicon:c/Ab3xY` (opaque) already carry the whole path.
    pathname = parsed.host === '' ? parsed.pathname : `/${parsed.host}${parsed.pathname}`;
    if (!pathname.startsWith('/')) pathname = `/${pathname}`;
  } else if (parsed.protocol === 'https:' && parsed.host === WEB_HOST) {
    // Universal links are https-only; nothing else may claim to be us.
    pathname = parsed.pathname;
  } else {
    return null;
  }

  const path = pathname.replace(/\/+$/, '') || '/';
  if (parseRoute(path).name !== 'challenge') return null;

  return `${path}${parsed.hash}`;
}
